import { EventContextProvider, useAnalytics } from "@codat/analytics";
import {
  Card,
  InfoBanner,
  Skeleton,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { loginSettingsQuery } from "api/login";
import { ParsedQuery, parse } from "query-string";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import formatUrl from "utils/formatUrl";
import { sanitizeRedirect } from "utils/sanitizeRedirect";
import { useQuery } from "utils/useQuery";
import { useTitle } from "utils/useTitle";

import AuthPage from "components/AuthPage";
import AxiosErrorDialog from "components/AxiosErrorDialog";

import styles from "./Login.module.scss";
import LoginByPassword from "./LoginByPassword";
import LoginByThirdParty from "./LoginByThirdParty";

export interface LoginProps {
  inviteId?: string;
  redirectUrl?: string;
  turnstileSiteKey?: string;
}

export interface LoginParams {
  redirectUrl?: string;
  errorMessage?: string;
}

const addLoginSuccessIfNeeded = (url?: string): string => {
  if (!url) {
    return window.location.origin + "/signin/success";
  } else if (url[0] === "/") {
    return (
      window.location.origin +
      formatUrl(
        "/signin/success",
        {},
        {
          redirectUrl: sanitizeRedirect(url),
        }
      )
    );
  } else {
    return sanitizeRedirect(url) as string;
  }
};

const Login: React.FC<LoginProps> = ({
  inviteId,
  redirectUrl: parentRedirectUrl,
}) => {
  const { addToast } = useToast();
  const { sendPageViewEvent } = useAnalytics();
  useTitle("Sign in");
  const query = useQuery();
  const [showInfoBanner] = useState(query.get("emailVerified") === "true");
  const history = useHistory();

  useEffect(() => {
    sendPageViewEvent("Sign in");
  }, [sendPageViewEvent]);

  useEffect(() => {
    if (query.get("emailVerified")) {
      query.delete("emailVerified");
      history.replace({ search: query.toString() });
    }
  }, [query, history]);

  const getRedirectUrl = (query: ParsedQuery<string>): string | undefined => {
    const possibleKeys = ["redirectUrl", "ReturnUrl", "returnUrl"];
    for (const possibleKey of possibleKeys) {
      const value = query[possibleKey];
      if (value) {
        return String(value);
      }
    }
  };

  const parsed = parse(location.search);

  // Only redirect to URL if it is on a Codat domain, otherwise redirect to portal
  const safeRedirectUrl = addLoginSuccessIfNeeded(
    parentRedirectUrl ?? getRedirectUrl(parsed)
  );
  const [errorMessage, setErrorMessage] = useState(
    parsed.errorMessage ? String(parsed.errorMessage) : ""
  );

  useEffect(() => {
    if (errorMessage) {
      addToast({
        message: `Unable to sign in: ${errorMessage}`,
        variant: "error",
      });
      setErrorMessage("");
    }
  }, [errorMessage, setErrorMessage, addToast]);

  const [
    {
      loading: loginSettingsLoading,
      error: loginSettingsError,
      data: loginSettings,
    },
  ] = loginSettingsQuery(inviteId);

  if (loginSettingsLoading) {
    return <Skeleton />;
  }

  if (loginSettingsError) {
    return (
      <AxiosErrorDialog
        error={loginSettingsError}
        activity={"getting sign-in settings"}
      />
    );
  }

  const hasLoginByPassword = !!loginSettings?.filter(
    (setting) => setting.isThirdParty === false
  )?.length;

  const thirdPartyProviders = loginSettings?.filter(
    (setting) => setting.isThirdParty === true
  );

  return (
    <AuthPage>
      {showInfoBanner && (
        <div className={styles.infoBanner}>
          <InfoBanner title="Thank you for verifying your sign-in method. Please sign in again." />
        </div>
      )}
      <Card className={styles.loginCard}>
        <Typography variant="heading_xl" as="h1">
          Sign in to Codat
        </Typography>
        {hasLoginByPassword && (
          <LoginByPassword redirectUrl={safeRedirectUrl} inviteId={inviteId} />
        )}
        {thirdPartyProviders && (
          <LoginByThirdParty
            loginSettings={thirdPartyProviders}
            inviteId={inviteId}
            redirectUrl={safeRedirectUrl}
          />
        )}
      </Card>
    </AuthPage>
  );
};

export default function LoginWithTracking(props: LoginProps) {
  return (
    <EventContextProvider name="Sign in">
      <Login {...props} />
    </EventContextProvider>
  );
}
